import {
    Box,
    Typography,
    Grid2,
    FormControl,
    Select,
    MenuItem,
    Grid,
    FormGroup,
    FormControlLabel,
    Checkbox,
    IconButton,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import DatePickerComponent from "../DatePicker/DatePicker";

import SaveIcon from "@mui/icons-material/Save";
import {
    HEALTH_LABEL,
    HEALTH_STARTING_TYPE,
    HEALTH_TYPE,
    PERMISSIONS,
} from "../../helper/constant";
import MuiTable from "../MuiTable/MuiTable";
import HealthDataChart from "../HealthDataChart/HealthDataChart";
import {
    formatDateTime,
    formatMinutesToHours,
    formatStepCount,
    hasAccess,
    httpRequest,
    onRequestError,
} from "../../helper/global";
import dayjs from "dayjs";
import { CheckBox } from "@mui/icons-material";
import { showAlert } from "../../reducers/alertSlice";
import { setPatientHealthData } from "../../reducers/patientSlice";

const TIME_RANGES = [
    { label: "7 Days", value: 7 },
    { label: "14 Days", value: 14 },
    { label: "30 Days", value: 30 },
    { label: "1 Year", value: 365 },
    { label: "Program Start", value: "programStart" },
    { label: "All time", value: "allTime" },
];

export default function WeightHistory({
    theme,
    user,
    permissionConfig,
    startDate,
    endDate,
    handleOnStartDateChange,
    handleOnEndDateChange,
    selectedRange,
    setSelectedRange,
    healthData = [],
    patient,
    dispatch,
    navigate,
}) {
    const handleRangeChange = (event) => {
        const value = event.target.value;
        setSelectedRange(value);

        const now = dayjs();
        let newStartDate;
        let newEndDate = now;

        switch (value) {
            case "allTime":
                newStartDate = null;
                break;

            case "programStart":
                newStartDate =
                    patient?.healthData?.startingWeightDate &&
                    patient.healthData.startingWeightDate !== ""
                        ? dayjs(patient.healthData.startingWeightDate)
                        : dayjs().subtract(7, "day");
                break;

            default:
                newStartDate = dayjs().subtract(value, "day");
                break;
        }

        handleOnStartDateChange(newStartDate);
        handleOnEndDateChange(newEndDate);
    };

    // const handleSetStartingData = async (row) => {
    //     try {
    //         let postData = {};

    //         if (row.bodyMass) {
    //             postData[`healthData.${HEALTH_STARTING_TYPE.startingWeight}`] =
    //                 row.bodyMass;
    //         }

    //         if (row.bodyFatPercentage) {
    //             postData[
    //                 `healthData.${HEALTH_STARTING_TYPE.startingBodyFatPercentage}`
    //             ] = row.bodyFatPercentage;
    //         }

    //         if (row.leanBodyMass) {
    //             postData[
    //                 `healthData.${HEALTH_STARTING_TYPE.startingLeanBodyMass}`
    //             ] = row.leanBodyMass;
    //         }

    //         const result = await httpRequest(
    //             `patient/update/${patient.id}`,
    //             postData,
    //             "POST",
    //             (error) => {
    //                 onRequestError(dispatch, navigate, {
    //                     title: error.title,
    //                     message: error.message,
    //                 });
    //             }
    //         );

    //         if (result === false) {
    //             return;
    //         }

    //         dispatch(
    //             showAlert({
    //                 title: "",
    //                 message: `Starting data updated successfully.`,
    //             })
    //         );

    //         let key = `${HEALTH_STARTING_TYPE.startingWeight}`;
    //         dispatch(setPatientHealthData({ [key]: row.bodyMass }));

    //         key = `${HEALTH_STARTING_TYPE.startingBodyFatPercentage}`;
    //         dispatch(setPatientHealthData({ [key]: row.bodyFatPercentage }));

    //         key = `${HEALTH_STARTING_TYPE.startingLeanBodyMass}`;
    //         dispatch(setPatientHealthData({ [key]: row.leanBodyMass }));
    //     } catch (error) {
    //         console.error("Error fetching data:", error);
    //     }
    // };

    const columns = [
        {
            id: "date",
            label: "Date",
            sort: true,
            visible: true,
            order: "desc",
            formatter: (value, row, index) => {
                if (!value) {
                    return "-";
                }

                return formatDateTime(value, "MMM D, YYYY", false);
            },
        },

        {
            id: HEALTH_TYPE.BODY_MASS,
            label: `${HEALTH_LABEL.bodyMass} (lbs)`,
            sort: true,
            visible: true,
        },

        {
            id: HEALTH_TYPE.BMI,
            label: HEALTH_LABEL.bmi,
            sort: true,
            visible: true,
        },

        {
            id: HEALTH_TYPE.BODY_FAT_PERCENTAGE,
            label: HEALTH_LABEL.bodyFatPercentage,
            sort: true,
            visible: true,
        },

        {
            id: HEALTH_TYPE.LEAN_BODY_MASS,
            label: `${HEALTH_LABEL.leanBodyMass} (lbs)`,
            sort: true,
            visible: true,
        },

        {
            id: HEALTH_TYPE.STEP_COUNT,
            label: HEALTH_LABEL.stepCount,
            sort: true,
            visible: true,
            formatter: formatStepCount,
        },

        {
            id: HEALTH_TYPE.SLEEP_ANALYSIS,
            label: HEALTH_LABEL.sleepAnalysis,
            sort: true,
            visible: true,
            formatter: (value, row, index) => {
                if (!value || value.length === "") {
                    return "-";
                }

                let sleepScoreText = "";

                if (row.breakdown && row.breakdown.sleepScore) {
                    sleepScoreText = ` (${Math.round(row.breakdown.sleepScore)} pts)`;
                }

                return `${formatMinutesToHours(value)}${sleepScoreText}`;
            },
            tooltip: function (value, row, index) {
                if (!value || value.length === "") {
                    return "";
                }

                if (!row.breakdown) {
                    return "";
                }

                let html = [];

                if (row.breakdown.startDate && row.breakdown.endDate) {
                    html.push(
                        <Typography variant="body2">
                            {`${formatDateTime(row.breakdown.startDate, "MMM D h:mm A", false)} - ${formatDateTime(row.breakdown.endDate, "MMM D h:mm A", false)}`}
                        </Typography>,
                    );
                }

                if (row.breakdown.inBedMinutes) {
                    html.push(
                        <Typography variant="body2">
                            {`In Bed: ${formatMinutesToHours(row.breakdown.inBedMinutes)}`}
                        </Typography>,
                    );
                }

                if (row.breakdown.coreMinutes) {
                    html.push(
                        <Typography variant="body2">
                            {`Core: ${formatMinutesToHours(row.breakdown.coreMinutes)}`}
                        </Typography>,
                    );
                }

                if (row.breakdown.remMinutes) {
                    html.push(
                        <Typography variant="body2">
                            {`REM: ${formatMinutesToHours(row.breakdown.remMinutes)}`}
                        </Typography>,
                    );
                }

                if (row.breakdown.deepMinutes) {
                    html.push(
                        <Typography variant="body2">
                            {`Deep: ${formatMinutesToHours(row.breakdown.deepMinutes)}`}
                        </Typography>,
                    );
                }

                if (row.breakdown.awakeMinutes) {
                    html.push(
                        <Typography variant="body2">
                            {`Awake: ${formatMinutesToHours(row.breakdown.awakeMinutes)}`}
                        </Typography>,
                    );
                }

                return html;
            },
        },

        {
            id: "sources",
            label: "Source",
            sort: false,
            visible: false,
            formatter: (value, row, index) => {
                if (!value || value.length === 0) {
                    return "-";
                }

                return value.join(", ");
            },
        },
        // {
        //     id: "",
        //     label: "Actions",
        //     sort: false,
        //     visible: true,
        //     formatter: (value, row, index) => {
        //         const buttons = [];

        //         if (
        //             row.bodyMass &&
        //             hasAccess(
        //                 user,
        //                 permissionConfig,
        //                 "patient",
        //                 PERMISSIONS.write
        //             )
        //         ) {
        //             buttons.push(
        // <IconButton
        //     aria-label="save"
        //     onClick={(event) => {
        //         event.stopPropagation();

        //         handleSetStartingData(row);
        //     }}
        //     title="Set as Starting Data"
        // >
        //     <SaveIcon />
        // </IconButton>
        //             );
        //         }

        //         return buttons;
        //     },
        // },
    ];

    const handleChartPointClick = (dataPoint) => {
        // Handle click on chart point
    };

    const [includeWeightManagement, setIncludeWeightManagement] =
        useState(true);

    const handleIncludeWeightManagementChange = (event) => {
        setIncludeWeightManagement(event.target.checked);
    };

    return (
        <>
            <Box>
                <Typography variant="h6">Health Data History</Typography>
                <Box
                    display={"flex"}
                    justifyContent={"right"}
                    alignItems={"center"}
                >
                    <Grid2 container spacing={2}>
                        <Grid2
                            item
                            xs={4}
                            display={"flex"}
                            alignItems={"center"}
                        >
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={includeWeightManagement}
                                            onChange={
                                                handleIncludeWeightManagementChange
                                            }
                                        />
                                    }
                                    label="Include Weight Management"
                                />
                            </FormGroup>
                        </Grid2>
                        <Grid2 item xs={4}>
                            <FormControl
                                variant="outlined"
                                sx={{ minWidth: 120 }}
                            >
                                <Select
                                    value={selectedRange}
                                    onChange={handleRangeChange}
                                    displayEmpty
                                    size="small"
                                    inputProps={{
                                        "aria-label": "Select Time Range",
                                    }}
                                >
                                    {TIME_RANGES.map((range) => (
                                        <MenuItem
                                            key={range.value}
                                            value={range.value}
                                        >
                                            {range.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid2>
                        <Grid2 item xs={4}>
                            <DatePickerComponent
                                defaultValue={startDate}
                                onChange={handleOnStartDateChange}
                            />
                        </Grid2>
                        <Grid2 item xs={4}>
                            <DatePickerComponent
                                defaultValue={endDate}
                                onChange={handleOnEndDateChange}
                            />
                        </Grid2>
                    </Grid2>
                </Box>
                <Box sx={{ mt: 3, mb: 3 }}>
                    <HealthDataChart
                        theme={theme}
                        healthData={healthData}
                        onPointClick={handleChartPointClick}
                        patient={patient}
                        includeWeightManagement={includeWeightManagement}
                    />
                </Box>

                <Box sx={{ mt: 3 }}>
                    <MuiTable
                        allowSearch={false}
                        theme={theme}
                        headCells={columns}
                        defaultData={healthData}
                        rowsPerPageArray={[50, 100, 200]}
                    />
                </Box>
            </Box>
        </>
    );
}
